import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import axios from "axios";
import { format } from "date-fns";

import { useLocation } from "react-router-dom";
import Footer from "../layout/Footer";
import styles from "./buddhism.module.sass";
import { NextArrow, PrevArrow } from "../components/sliderArrow";
import Navbar from "../layout/Navbar";
import ImageModal from "../components/ImageModal";
import { renderMedia, isVideoFile } from "../utils/mediaUtils";

function BuddhismIntro() {
  const isMounted = useRef(false);
  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const [buddhismName, setBuddhismName] = useState("");
  const [buddhismContent, setBuddhismContent] = useState("");
  const [pictureList, setPictureList] = useState(null);
  const [lessonList, setLessonList] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const mainSliderContainerRef = useRef(null);

  let location = useLocation();
  const pageId = useMemo(() => new URLSearchParams(location.search).get("id"), [location.search]);

  const fetchPageData = useCallback(async (id) => {
    try {
      const response = await axios.post("/api/website/getBuddhism", { id: id * 1 });

      setBuddhismName(response.data.data.data.name);
      setBuddhismContent(response.data.data.data.content);
      setPictureList(response.data.data.slider);
      setLessonList(response.data.data.lesson);
    } catch (error) {
      console.error("Error fetching article content:", error);
    }
  }, []);

  // 當主輪播索引改變時，若前一張或當前是影片，則確保非當前的影片被暫停
  useEffect(() => {
    if (!mainSliderContainerRef.current) return;
    const videos = mainSliderContainerRef.current.querySelectorAll('video');
    videos.forEach((vid) => {
      // 直接嘗試暫停所有影片，當前若需要播放由使用者操作
      if (!vid.paused) {
        if (typeof vid.pause === 'function') {
          vid.pause();
        }
      }
    });
  }, [currentSlideIndex]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      fetchPageData(pageId);
    }
  }, [fetchPageData, pageId, location.search]);

  if (pictureList === null) {
    return;
  }

  const handleImageClick = () => {
    const newIndex = currentSlideIndex;
    const current = pictureList[newIndex];
    setModalImageSrc(current.image);
    setModalImageAlt(current.title || "");
    setModalImageIndex(newIndex);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const handlePreviousImage = () => {
    if (!pictureList || pictureList.length === 0) return;
    const total = pictureList.length;
    const newIndex = (modalImageIndex - 1 + total) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(pictureList[newIndex].image);
    setModalImageAlt(pictureList[newIndex].title || "");
  };

  const handleNextImage = () => {
    if (!pictureList || pictureList.length === 0) return;
    const total = pictureList.length;
    const newIndex = (modalImageIndex + 1) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(pictureList[newIndex].image);
    setModalImageAlt(pictureList[newIndex].title || "");
  };

  return (
    <>
      <Navbar path={pageId} />
      <main>
        <section className="h-[250px] page-title flex justify-center items-center">
          <h2 className="text-[#3D3D3D] font-semibold text-5xl table:text-4xl mobile:text-3xl">
            {buddhismName}
          </h2>
        </section>
        <section>
          <div className="max-w-[1600px] mx-auto">
            <div className="px-10 py-20 flex flex-row gap-10 table:flex-col-reverse table:gap-10 table:px-10 table:py-12 mobile:px-8 mobile:py-10 mobile:flex-col-reverse mobile:gap-6">
              {pictureList.length > 0 && (
                <div className="w-[1000px] table:w-full mobile:w-full">
                  <div ref={mainSliderContainerRef} className="w-[86%] mx-auto table:w-full mobile:w-full">
                    <Slider
                      asNavFor={nav2}
                      ref={(slider) => setNav1(slider)}
                      dots={false}
                      fade={true}
                      swipeToSlide={true}
                      focusOnSelect={true}
                      accessibility={false}
                      slidesToShow={1}
                      slidesToScroll={1}
                      infinite={true}
                      beforeChange={(oldIndex, newIndex) => setCurrentSlideIndex(newIndex)}
                      prevArrow={<PrevArrow customClassName={styles.prevArrow} />}
                      nextArrow={<NextArrow customClassName={styles.nextArrow} />}
                    >
                      {pictureList.map((item) => {
                        return (
                          <div role="button" key={item.id} data-id={item.id} className="border">
                            <div className="aspect-[860/600] relative bg-black">
                              {renderMedia(
                                item.image,
                                item.title || "preview-img",
                                "absolute inset-0 w-full h-full object-contain cursor-zoom-in",
                                {},
                                false,
                                true,
                                () => handleImageClick(),
                                true
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </Slider>
                  </div>
                </div>
              )}
              <div className="flex-1 text-2xl text-[#4B4A4A] flex flex-col gap-2 items-end table:w-full table:p-0 table:text-xl mobile:text-base mobile:p-0">
                <p dangerouslySetInnerHTML={{ __html: buddhismContent }} />
              </div>
            </div>
          </div>
          {pictureList.length > 0 && (
            <div>
              <Slider
                asNavFor={nav1}
                ref={(slider) => setNav2(slider)}
                dots={false}
                slidesToShow={8}
                centerMode={pictureList.length > 8}
                infinite={pictureList.length > 8}
                swipeToSlide={true}
                focusOnSelect={true}
                accessibility={false}
                slidesToScroll={1}
                prevArrow={null}
                nextArrow={null}
                responsive={[
                  {
                    breakpoint: 1350,
                    settings: {
                      slidesToShow: 6,
                      centerMode: pictureList.length > 6,
                      infinite: pictureList.length > 6,
                    },
                  },
                  {
                    breakpoint: 991,
                    settings: {
                      slidesToShow: 5,
                      centerMode: pictureList.length > 5,
                      infinite: pictureList.length > 5,
                    },
                  },
                  {
                    breakpoint: 766,
                    settings: {
                      slidesToShow: 4,
                      centerMode: pictureList.length > 4,
                      infinite: pictureList.length > 4,
                    },
                  },
                  {
                    breakpoint: 375,
                    settings: {
                      slidesToShow: 3,
                      centerMode: pictureList.length > 3,
                      infinite: pictureList.length > 3,
                    },
                  },
                ]}
              >
                {pictureList.map((item, index) => {
                  return (
                    <div
                      role="button"
                      key={item.id}
                      data-id={item.id}
                      className="border"
                      onClick={() => {
                        if (nav1 && typeof nav1.slickGoTo === "function") {
                          nav1.slickGoTo(index, true);
                          setCurrentSlideIndex(index);
                        }
                      }}
                    >
                      <div className="aspect-[860/600] relative bg-black">
                        {renderMedia(
                          item.image,
                          item.title || "preview-img",
                          "absolute inset-0 w-full h-full object-contain",
                          {},
                          false,
                          false,
                          null,
                          false
                        )}
                      </div>
                    </div>
                  );
                })}
              </Slider>
            </div>
          )}
        </section>
        <section className="max-w-[800px] mx-auto">
          <div className="py-16 px-10 mobile:px-4 mobile:py-6">
            <h2 className="text-[#3D3D3D] text-center font-semibold text-5xl table:text-4xl mobile:text-3xl font-notoSerifTC mb-8 flex justify-center items-center flex-col">
              <span className="block aspect-[355/26] w-[347px] bg-cover bg-center bg-no-repeat bg-titleDecoLine mobile:w-[80%]" />
              <p className="my-2">課程列表</p>
              <span className="block aspect-[355/26] w-[347px] bg-cover bg-center bg-no-repeat bg-titleDecoLine mobile:w-[80%]" />
            </h2>
            <ul className="[*&>:first-child]:pt-0">
              {lessonList.map((lesson) => (
                <li key={lesson.id} className="py-5 border-b-[1px] border-b-gray-200">
                  <h4 className="font-bold text-2xl font-notoSerifTC text-[#3D3D3D]">
                    {lesson.name}
                  </h4>
                  {!lesson.startTime && !lesson.endTime ? (  
                    <p className="text-gray-600"></p>
                  ) : (
                    <p className="text-gray-600">{`${lesson.startTime ? format(new Date(lesson.startTime), "yyyy-MM-dd") : ""} ~ ${lesson.endTime ? format(new Date(lesson.endTime), "yyyy-MM-dd") : ""}`}</p>
                  )}
                  <div className="flex justify-end mt-4">
                    <a
                      href={`/buddhismLesson?id=${lesson.id}`}
                      className="text-xl px-6 py-3 rounded bg-yellow-400 mobile:w-full hover:bg-yellow-500 duration-200 cursor-pointer text-center"
                    >
                      查看課程細節
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={closeImageModal}
        images={pictureList}
        currentIndex={modalImageIndex}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />
      <Footer />
    </>
  );
}

export default BuddhismIntro;
